import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useLocation, useNavigate, useParams } from "react-router-dom";

import { CampaignDisplayStatus, type CampaignStat } from "@signalist/shared-types";

import {
	useGetCampaignStatsQuery,
	useGetListQuery,
	useUpdateCampaignStatusMutation,
} from "@/pages/Private/redux/list/list.api";

import {
	useGetListOutreachConfigQuery,
	useUpdateListOutreachConfigMutation,
} from "@/pages/Private/redux/list/list-outreach.api";

import { listOutreachConfigSelector } from "@/pages/Private/redux/list/list-outreach.slice";

import { useAppSelector } from "@/redux/hooks";

import { CampaignStatus } from "@/enum/campaign-status.enum";

import { LoadingOverlay, Modal } from "@/components";

import { useIsFeatureActive } from "@/utils/useIsFeatureActive";
import { useSnackBar } from "@/utils/useSnackbar";

import { Button } from "@/components/Button/Button";

import { ButtonColor } from "@/components/Button/types";

import { ListSourceType } from "@/enum/list.enum";

import { CsvEnrichmentType } from "@/enum/csv-enrichment.enum";

import { ScrapeLevel } from "@/enum/job.enum";

import { useCampaignValidation } from "@/pages/Private/pages/List/components/CampaignOverview/utils/useCampaignValidation";
import { eventStopDatePrefill } from "@/pages/Private/pages/List/components/CampaignOverview/utils/eventStopDatePrefill.util";

import { ListBuilderLayout } from "../../components/ListBuilderLayout";

import { getAvailableSteps, isDetailsNeeded, getDoneSteps } from "../../utils/create-list-steps";
import { Stepper } from "../ListEditDetails/components/Stepper";
import { FeaturesCodes } from "../../../Users/enum/codes";
import { CampaignOverview } from "../../components/CampaignOverview/CampaignOverview";
import { Campaign } from "../../schema/campaign";
import { GlobalNodesDataProvider } from "../../components/CampaignOverview/components/NodesContextProvider";
import { Sender } from "../../components/CampaignOverview/components/Sender";
import { Schedule } from "../../components/CampaignOverview/components/Schedule";
import { CreateCampaignModal } from "../../components/CampaignOverview/components/CreateCampaignModal";
import { DeleteCampaignModal } from "../../components/CampaignOverview/components/DeleteCampaignModal";
import { CopyCampaignsSelector } from "../../components/CopyCampaignsSelector/CopyCampaignsSelector";
import { CampaignSocketProvider } from "../../providers/CampaignSocketContext";
import { CampaignBranchModal } from "../../components/EventOutreach/CampaignBranchModal";

import { CampaignsSection } from "./components/CampaignsSection";
import { ChatSection } from "./components/ChatSection";
import { GeneralSection } from "./components/GeneralSection";
import { OutreachSkipBar } from "./components/OutreachSkipBar";
import { OutreachSubHeader } from "./components/OutreachSubHeader";
import { campaignStatusErrorText } from "./utils/campaignStatusErrorText";
import { ApplyEventStopDateModal } from "./components/ApplyEventStopDateModal";
import {
	getCampaignNameFromUrl,
	getCampaignUuidFromUrl,
	shouldShowCampaignBuilder,
} from "./ListEditCopywriting.helpers";
import { useAutoCreateCampaignParam } from "./useAutoCreateCampaignParam";
import { useCampaignActions } from "./useCampaignActions";
import { isEventOutreachEligible } from "./utils/eventOutreachEligibility";
import { listCampaignsEnabled } from "../../components/ReachOut/reachOutCampaignsEnabled";

export type CopywritingCampaign = {
	id: string;
	name: string;
	isDefault: boolean;
	isValid: boolean;
	isEventBase?: boolean;
	eventAt?: Date | null;
	pausePeopleFromTheSameCompany?: boolean;
	trackClicks?: boolean;
	trackOpens?: boolean;
	stopCampaignAt?: Date | null;
	timezone?: string;
	schedule?: Schedule;
	senders?: Sender[];
	languages?: string[];
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	initialNodes: any[];
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	initialEdges: any[];
};

export const ListEditCopywriting: FunctionComponent = () => {
	const { t } = useTranslation();
	const { id = 0 } = useParams();

	const [showNewCampaignModal, setShowNewCampaignModal] = useState<boolean>(false);
	const { data, isLoading, refetch } = useGetListQuery(+id, { refetchOnMountOrArgChange: true });
	const { data: campaignStats } = useGetCampaignStatsQuery({ listId: +id });
	// Reasons behind each campaign's INVALID badge (PREC-3952) — the stored flag is only a boolean.
	const campaignValidation = useCampaignValidation(data);

	// The outreach config (PREC-3687): fetched here, consumed from the slice (web rule §2). It is
	// not on the `List` payload's type — `schema/list.ts` is at the length ratchet and cannot
	// declare it — so the step reads its own bucket.
	useGetListOutreachConfigQuery(+id);
	const outreachConfig = useAppSelector(listOutreachConfigSelector(+id));
	const [updateOutreachConfig] = useUpdateListOutreachConfigMutation();

	// While a single-campaign activation is rescheduling, the list rollup is LOADING; poll so the
	// campaign flips LOADING → ACTIVE here once the reschedule finishes (mirrors the list table).
	useEffect(() => {
		if (data?.campaignStatus !== CampaignStatus.LOADING) {
			return;
		}

		const interval = setInterval(() => refetch(), 5000);

		return () => clearInterval(interval);
	}, [data?.campaignStatus, refetch]);

	// campaignId -> its stats, for the numeric columns + totals.
	const statsByCampaign = useMemo(() => {
		const map: Record<string, CampaignStat> = {};

		campaignStats?.data?.forEach((stat) => {
			map[stat.campaignId] = stat;
		});

		return map;
	}, [campaignStats]);

	const location = useLocation();
	const params = new URLSearchParams(location.search);
	const isAutoDirected = params.get("autoDirected") === "true";

	const [currentlySelectedCampaign, setCurrentlySelectedCampaign] = useState<string>(
		getCampaignNameFromUrl(location.search, data?.campaignConfig)
	);

	const [currentlySelectedCampaignUuid, setCurrentlySelectedCampaignUuid] = useState<string>(
		getCampaignUuidFromUrl(location.search, data?.campaignConfig)
	);

	const navigate = useNavigate();
	const ts = (key: string) => t(`list.${key}`);

	const isSequencerActive = useIsFeatureActive(FeaturesCodes.SEQUENCER);

	const [updateCampaignStatus, { isLoading: isTogglingCampaignStatus }] =
		useUpdateCampaignStatusMutation();
	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();

	// Pause/resume a single campaign from the campaigns table (PREC-3249).
	const handleToggleCampaignRunStatus = async (
		campaignUuid: string,
		currentStatus: CampaignDisplayStatus
	) => {
		const nextStatus =
			currentStatus === CampaignDisplayStatus.ACTIVE
				? CampaignStatus.PAUSED
				: CampaignStatus.RUNNING;

		try {
			await updateCampaignStatus({ id: +id, campaignUuid, status: nextStatus }).unwrap();
		} catch (error) {
			// PREC-4320: a rejected mutation reaches nobody on its own (the global middleware handles
			// rejected queries only), so a campaign whose end date had passed simply did not start.
			handleOpenSnackbar(
				campaignStatusErrorText(error as Parameters<typeof campaignStatusErrorText>[0]),
				8000
			);
		}
	};

	// Persist the "Enable campaigns on this list" toggle. The section pre-checks the O4 disable guard
	// before calling this; the backend rejects a disable that slipped through anyway.
	const handleCampaignsEnabledChange = async (enabled: boolean) => {
		try {
			await updateOutreachConfig({ listId: +id, body: { campaignsEnabled: enabled } }).unwrap();
		} catch {
			// Errors surfaced by the global RTK Query error middleware.
		}
	};

	const [isModalOpened, setIsModalOpened] = useState(false);
	const [isCopyModalOpened, setIsCopyModalOpened] = useState(false);
	const [showBranchModal, setShowBranchModal] = useState(false);
	// Bumped to open the event-details modal from the create-campaign "Event" branch.
	const [eventOpenSignal, setEventOpenSignal] = useState(0);
	// True while the "Event" branch is mid-flight, so saving the event continues into campaign creation.
	const [createAfterEvent, setCreateAfterEvent] = useState(false);
	// Event end date to prefill as the new campaign's stop date when event outreach is active.
	const [newCampaignStopPrefill, setNewCampaignStopPrefill] = useState<string | null>(null);

	useEffect(() => {
		if (!data) {
			return;
		}

		const params = new URLSearchParams(location.search);
		const campaignUuid = params.get("campaignUuid");
		const campaign = params.get("campaign");

		if (campaignUuid && campaign && data.campaignConfig?.[campaignUuid]) {
			setCurrentlySelectedCampaignUuid(campaignUuid);
			setCurrentlySelectedCampaign(campaign);
			setIsModalOpened(true);
		}
	}, [data, location.search]);
	const handleSetCurrentlySelectedCampaignFromCampaignOverview = (campaignUuid: string) => {
		if (campaignUuid === currentlySelectedCampaignUuid) {
			return;
		}

		// Just the selection. This used to close and reopen the builder around the change to force a
		// teardown, which React 18 batched into a no-op — the builder is already open, and the canvas
		// gets its clean mount from the `key` on <CampaignPage> (PREC-4194).
		setCurrentlySelectedCampaignUuid(campaignUuid);
	};

	const campaignConfig = useMemo(() => data?.campaignConfig, [data]);

	const campaignActions = useCampaignActions({
		listId: +id,
		campaignConfig,
		eventEndDate: data?.event?.endDate,
		copyStopDate: eventStopDatePrefill(data?.event),
	});

	useEffect(() => {
		if (campaignConfig && currentlySelectedCampaignUuid) {
			const campaign = campaignConfig[currentlySelectedCampaignUuid];

			if (campaign && campaign.name && campaign.name !== currentlySelectedCampaign) {
				setCurrentlySelectedCampaign(campaign.name);
			}
		}
	}, [campaignConfig, currentlySelectedCampaignUuid, currentlySelectedCampaign]);

	const handleRowClick = (campaignUuid: string) => {
		setCurrentlySelectedCampaign(campaignConfig?.[campaignUuid]?.name as string);
		setCurrentlySelectedCampaignUuid(campaignUuid);
		setIsModalOpened(true);
	};

	const handleSetCurrentlySelectedCampaign = async (campaign: string, campaignUuid: string) => {
		// Called by CreateCampaignModal right after it persists the new campaign. Await the list
		// refetch so the builder mount gate (which now requires the campaign to exist in config)
		// passes immediately instead of waiting for the cache-invalidation refetch to land.
		await refetch();

		setCurrentlySelectedCampaign(campaign);
		setCurrentlySelectedCampaignUuid(campaignUuid);
		setIsModalOpened(true);
	};

	// Open the campaign builder (or create a default campaign when the list has none yet).
	// When event outreach is active, prefill the new campaign's stop date with the event end date.
	// `eventEndOverride` carries the just-saved end date in the create-via-event-branch flow, where
	// the refetched `data.event` may not be fresh yet.
	const proceedToCreateCampaign = (eventEndOverride?: string | null) => {
		const stopDate = eventEndOverride ?? eventStopDatePrefill(data?.event);

		// Always open the create-campaign modal — including for the very first campaign — so the user
		// names and configures it (name, senders, schedule, …) instead of getting an auto-named
		// "Default Campaign". The backend merges the new campaign into any existing config (PREC-3532).
		setNewCampaignStopPrefill(stopDate);
		setShowNewCampaignModal(true);
	};

	// Before the user picks "Event outreach" once (no list_event row), ask Event vs Regular
	// (PREC-3520) — but only on a list type that offers event outreach at all (PREC-3687 D6/AC 5).
	// For every other type the branch has no second option, so it goes straight to creation.
	const handleCreateCampaignClick = () => {
		if (data?.event || !isEventOutreachEligible(data?.sourceType)) {
			proceedToCreateCampaign();
		} else {
			setShowBranchModal(true);
		}
	};

	// Auto-open the Event-vs-Regular branch picker when arriving via the overview's hover action.
	useAutoCreateCampaignParam(!!data, handleCreateCampaignClick);

	const goToSettings = () => navigate(`/app/lists/create/settings/${id}`);

	const previousStepPath = () => {
		if (data?.sourceType === ListSourceType.TRACKER) {
			return `/app/lists/create/details/${id}`;
		}

		if (
			data?.entityType === CsvEnrichmentType.ORGANIZATION ||
			data?.scrapeLevel === ScrapeLevel.GET_ENRICHMENT ||
			data?.sourceType === ListSourceType.PARALLEL_AI
		) {
			return `/app/lists/create/details/${id}`;
		}

		return `/app/lists/create/source/${id}`;
	};

	return isLoading || !data ? (
		<LoadingOverlay />
	) : (
		<ListBuilderLayout bodyClassName="w-full p-4 pb-32" sourceTypeLabel={ts(data.sourceType)}>
			<Stepper
				availableSteps={getAvailableSteps(isDetailsNeeded(data), data, isSequencerActive)}
				currentStep="copywriting"
				doneSteps={getDoneSteps(data)}
				isDirty={false}
				listId={data.id}
				sourceType={data.sourceType}
			/>
			<OutreachSubHeader listId={data.id} />
			<OutreachSkipBar listId={id} />
			<div className="w-full">
				<GeneralSection
					event={data.event}
					eventOpenSignal={eventOpenSignal}
					listColumns={data.columns}
					listId={data.id}
					sourceType={data.sourceType}
					onEventModalClosed={() => setCreateAfterEvent(false)}
					onEventSaved={(payload) => {
						if (createAfterEvent) {
							setCreateAfterEvent(false);
							proceedToCreateCampaign(payload.endDate);
						}
					}}
				/>
				<CampaignsSection
					campaignValidation={campaignValidation}
					campaignsEnabled={listCampaignsEnabled(data.sourceType, outreachConfig)}
					isTogglingCampaignStatus={isTogglingCampaignStatus}
					listData={data}
					statsByCampaign={statsByCampaign}
					statsLoaded={!!campaignStats}
					onApplyEventStopDate={campaignActions.applyEventStopDate}
					onCampaignsEnabledChange={handleCampaignsEnabledChange}
					onCloneCampaign={campaignActions.cloneCampaign}
					onCopyFromList={() => setIsCopyModalOpened(true)}
					onCreateCampaign={handleCreateCampaignClick}
					onDeleteCampaign={campaignActions.openDeleteModal}
					onRowClick={handleRowClick}
					onSetAsDefault={campaignActions.openSetAsDefaultModal}
					onSkip={goToSettings}
					onToggleCampaignRunStatus={handleToggleCampaignRunStatus}
				/>
				<ChatSection listId={data.id} sourceType={data.sourceType} />
			</div>

			<div className="absolute mt-14 h-[72px] flex flex-row justify-end items-center  border-t border-t-border px-8 py-[14px] rounded-t-16 bottom-0 right-0 z-20 bg-white w-full">
				<div className="flex flex-row self-left items-center gap-x-4">
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						title={t("basics.previous")}
						onClick={() => navigate(previousStepPath())}
					/>
					<Button title={t("basics.next")} onClick={goToSettings} />
				</div>
			</div>
			{shouldShowCampaignBuilder(isModalOpened, campaignConfig, currentlySelectedCampaignUuid) && (
				<GlobalNodesDataProvider>
					<CampaignSocketProvider campaignId={currentlySelectedCampaignUuid} listId={+id}>
						<CampaignOverview
							currentlySelectedCampaign={currentlySelectedCampaign}
							currentlySelectedCampaignUuid={currentlySelectedCampaignUuid}
							handleClose={() => {
								setIsModalOpened(false);
								if (isAutoDirected) {
									const returnTo = sessionStorage.getItem("campaignEditReturnTo");

									sessionStorage.removeItem("campaignEditReturnTo");
									navigate(returnTo || `/app/lists/create/copywriting/${id}`, { replace: true });
								} else {
									navigate(`/app/lists/create/copywriting/${id}`, { replace: true });
								}
							}}
							isOpened={isModalOpened}
							listData={data}
							setCurrentlySelectedCampaign={setCurrentlySelectedCampaign}
							setCurrentlySelectedCampaignUuid={
								handleSetCurrentlySelectedCampaignFromCampaignOverview
							}
						/>
					</CampaignSocketProvider>
				</GlobalNodesDataProvider>
			)}
			{isCopyModalOpened && (
				<CopyCampaignsSelector
					handleClose={() => setIsCopyModalOpened(false)}
					handleSelect={(campaigns: Campaign[]) => {
						setIsCopyModalOpened(false);
						campaignActions.duplicateCampaigns(campaigns);
					}}
					isOpened={isCopyModalOpened}
					listId={+id}
				/>
			)}
			{campaignActions.showDeleteModal && campaignActions.selectedCampaign && (
				<DeleteCampaignModal
					campaignName={campaignActions.selectedCampaign.name}
					campaignUuid={campaignActions.selectedCampaign.id}
					listId={data.id}
					onClose={campaignActions.closeDeleteModal}
					onDeleted={campaignActions.onCampaignDeleted}
				/>
			)}
			{campaignActions.showSetAsDefaultModal && campaignActions.selectedCampaign && (
				<Modal
					handleClose={campaignActions.closeSetAsDefaultModal}
					handleSave={() =>
						campaignActions.setAsDefaultCampaign(
							campaignActions.selectedCampaign as CopywritingCampaign
						)
					}
					isOpened={campaignActions.showSetAsDefaultModal}
					title={"Set as default campaign"}
				>
					<div className="text-sm text-gray-700 mb-4">
						Are you sure you want to set this campaign as default?
					</div>
				</Modal>
			)}
			<CreateCampaignModal
				defaultStopCampaignAt={newCampaignStopPrefill}
				handleClose={() => setShowNewCampaignModal(false)}
				isOpened={showNewCampaignModal}
				listData={data}
				setCurrentlySelectedCampaign={handleSetCurrentlySelectedCampaign}
			/>
			<ApplyEventStopDateModal
				endDate={data.event?.endDate ?? ""}
				isOpened={campaignActions.showApplyStopDateModal}
				onClose={campaignActions.closeApplyStopDateModal}
				onConfirm={campaignActions.confirmApplyStopDate}
				overwrites={campaignActions.stopDateOverwrites}
			/>
			<CampaignBranchModal
				isOpened={showBranchModal}
				onChooseEvent={() => {
					setShowBranchModal(false);
					setCreateAfterEvent(true);
					setEventOpenSignal((signal) => signal + 1);
				}}
				onChooseRegular={() => {
					setShowBranchModal(false);
					proceedToCreateCampaign();
				}}
				onClose={() => setShowBranchModal(false)}
			/>
			{renderMessageSnackbar()}
		</ListBuilderLayout>
	);
};
